import React, { useState, useRef, useCallback } from 'react';
import { useGooglePlacesAutocomplete } from '../../hooks/useGooglePlacesAutocomplete';

const MailInCheckoutModal = ({ 
  isOpen, 
  onClose, 
  packageDetails, 
  onSuccess 
}) => {
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    email: '',  // Added email field to initial state
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    phone: ''
  });
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [validating, setValidating] = useState(false);
  const [addressValidated, setAddressValidated] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [addressFromGooglePlaces, setAddressFromGooglePlaces] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Google Places Autocomplete handler - MEMOIZED to prevent re-initialization
  const handlePlaceSelected = useCallback((addressData) => {
    console.log('🏠 Google Places data received:', addressData); // Debug log
    console.log('🏠 Extracted values:', {
      street1: addressData.street1,
      street2: addressData.street2,
      city: addressData.city,
      state: addressData.state,
      zip: addressData.zip
    });
    
    // CRITICAL: ALWAYS overwrite address fields with Google data
    // This ensures city/state/zip REPLACE any existing values
    setShippingAddress(prev => {
      console.log('🏠 Previous address state:', prev);
      const newState = {
        ...prev,  // Preserve name, email, phone
        street1: addressData.street1 || '',
        street2: addressData.street2 || '',  // Clear if not provided by Google
        city: addressData.city || '',  // ALWAYS overwrite, even if empty
        state: addressData.state || '',  // ALWAYS overwrite, even if empty
        zip: addressData.zip || ''  // ALWAYS overwrite, even if empty
      };
      console.log('🏠 New address state after Google selection:', newState);
      return newState;
    });
    
    console.log('✅ Address fields updated from Google Places');
    
    // Mark address as from Google Places, but still needs validation
    // Google may suggest addresses that aren't actually deliverable
    setAddressFromGooglePlaces(true);
    setAddressValidated(false);
  }, []); // Empty dependency array - function never changes

  // Initialize Google Places Autocomplete
  const { inputRef: street1Ref } = useGooglePlacesAutocomplete(handlePlaceSelected);

  if (!isOpen || !packageDetails) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress(prev => ({
      ...prev,
      [name]: value
    }));
    // Reset validation when user manually edits address
    if (['street1', 'street2', 'city', 'state', 'zip'].includes(name)) {
      setAddressValidated(false);
      setAddressFromGooglePlaces(false); // Address was modified, no longer from Google
    }
  };

  const validateForm = () => {
    // Email validation
    if (!shippingAddress.email || !shippingAddress.email.trim()) {
      setError('Please enter your email address');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shippingAddress.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    // Name validation (at least 2 words, minimum 3 characters total)
    if (!shippingAddress.name.trim()) {
      setError('Please enter your full name');
      return false;
    }
    if (shippingAddress.name.trim().length < 3) {
      setError('Please enter a valid full name');
      return false;
    }
    
    // Street address validation (minimum 5 characters)
    if (!shippingAddress.street1.trim()) {
      setError('Please enter your street address');
      return false;
    }
    if (shippingAddress.street1.trim().length < 5) {
      setError('Please enter a complete street address (e.g., 123 Main St)');
      return false;
    }
    
    // City validation (minimum 2 characters, only letters and spaces)
    if (!shippingAddress.city.trim()) {
      setError('Please enter your city');
      return false;
    }
    if (shippingAddress.city.trim().length < 2) {
      setError('Please enter a valid city name');
      return false;
    }
    if (!/^[a-zA-Z\s\-]+$/.test(shippingAddress.city.trim())) {
      setError('City name should only contain letters');
      return false;
    }
    
    // State validation (exactly 2 letters)
    if (!shippingAddress.state.trim()) {
      setError('Please enter your state');
      return false;
    }
    if (!/^[A-Z]{2}$/i.test(shippingAddress.state.trim())) {
      setError('Please enter a valid 2-letter state code (e.g., VA, NY, CA)');
      return false;
    }
    
    // ZIP code validation (5 digits, optionally followed by -4 digits)
    if (!shippingAddress.zip.trim()) {
      setError('Please enter your ZIP code');
      return false;
    }
    if (!/^\d{5}(-\d{4})?$/.test(shippingAddress.zip.trim())) {
      setError('Please enter a valid ZIP code (e.g., 12345 or 12345-6789)');
      return false;
    }
    
    return true;
  };

  const validateShippingAddress = async () => {
    setError('');
    
    // Comprehensive client-side validation for all addresses
    const street = shippingAddress.street1.trim();
    const city = shippingAddress.city.trim();
    const state = shippingAddress.state.trim();
    const zip = shippingAddress.zip.trim();
    
    // 1. Street validation
    if (!street || street.length < 5) {
      setError('Please enter a complete street address.');
      return false;
    }
    
    // Street must contain a number (house/building number)
    if (!/\d/.test(street)) {
      setError('Street address must include a street number (e.g., 123 Main St).');
      return false;
    }
    
    // Street should have at least a number and a street name
    const streetParts = street.split(/\s+/);
    if (streetParts.length < 2) {
      setError('Please enter a complete street address with number and street name.');
      return false;
    }
    
    // 2. City validation
    if (!city || city.length < 2) {
      setError('Please enter a valid city name.');
      return false;
    }
    
    // City should only contain letters, spaces, and hyphens
    if (!/^[a-zA-Z\s\-'.]+$/.test(city)) {
      setError('City name contains invalid characters.');
      return false;
    }
    
    // 3. State validation
    if (!state || !/^[A-Z]{2}$/i.test(state)) {
      setError('Please enter a valid 2-letter state code (e.g., VA, CA, NY).');
      return false;
    }
    
    // 4. ZIP validation
    if (!zip || !/^\d{5}(-\d{4})?$/.test(zip)) {
      setError('Please enter a valid ZIP code (e.g., 12345 or 12345-6789).');
      return false;
    }
    
    // All validation passed
    setAddressValidated(true);
    return true;
  };
  
  const acceptCorrectedAddress = () => {
    if (validationResult?.corrected) {
      setShippingAddress(prev => ({
        ...prev,
        street1: validationResult.corrected.street1,
        street2: validationResult.corrected.street2 || '',
        city: validationResult.corrected.city,
        state: validationResult.corrected.state,
        zip: validationResult.corrected.postal_code
      }));
      setAddressValidated(true);
      setShowCorrectionModal(false);
    }
  };
  
  const keepOriginalAddress = () => {
    setAddressValidated(true);
    setShowCorrectionModal(false);
  };

  const handleCheckout = async () => {
    setError('');
    
    if (!validateForm()) {
      return;
    }
    
    // Validate address before checkout using USPS (FREE!)
    if (!addressValidated) {
      const isValid = await validateShippingAddress();
      if (!isValid) {
        return;
      }
      // If correction modal is shown, wait for user decision
      if (showCorrectionModal) {
        return;
      }
    }

    setProcessing(true);

    try {
      // Get user_id from localStorage
      const userDataString = localStorage.getItem('user');
      const userData = userDataString ? JSON.parse(userDataString) : null;
      const userId = userData?.id;
      
      if (!userId) {
        setError('User not authenticated. Please log in again.');
        return;
      }
      
      // Create checkout session
      const response = await fetch(`${API_BASE_URL}/api/mailin-service/create-checkout?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          package_id: packageDetails.packageId,
          addon_credits: packageDetails.addonCredits || 0,
          shipping_address: shippingAddress
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect directly to Stripe checkout URL
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Failed to process checkout. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const addonPrice = packageDetails.addonCredits ? 
    (packageDetails.addonCredits === 25 ? 5 :
     packageDetails.addonCredits === 50 ? 8 :
     packageDetails.addonCredits === 100 ? 15 :
     packageDetails.addonCredits === 250 ? 34 :
     packageDetails.addonCredits === 500 ? 59 : 0) : 0;
  
  const totalPrice = packageDetails.basePrice + packageDetails.shipping + addonPrice;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header - Mobile Optimized */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-4 sm:p-6 text-white sticky top-0 z-10">
          <div className="flex justify-between items-start gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold">Complete Your Order</h2>
              <p className="text-purple-100 text-xs sm:text-sm mt-1">Mail-In Photo Digitization Service</p>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors flex-shrink-0"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {/* Process Steps Visual - Mobile Optimized */}
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 sm:p-6 border border-blue-200">
            <h4 className="text-center font-semibold text-gray-800 mb-4 text-sm sm:text-base">How It Works</h4>
            
            {/* Desktop Layout */}
            <div className="hidden sm:flex items-center justify-between text-xs">
              {/* Step 1 */}
              <div className="flex-1 text-center">
                <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold shadow-lg">
                  1
                </div>
                <p className="font-medium text-gray-700">Submit Payment</p>
              </div>
              
              {/* Arrow */}
              <div className="flex-shrink-0 px-2">
                <svg className="w-4 h-4 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
              
              {/* Step 2 */}
              <div className="flex-1 text-center">
                <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold shadow-lg">
                  2
                </div>
                <p className="font-medium text-gray-700">Receive Kit</p>
              </div>
              
              {/* Arrow */}
              <div className="flex-shrink-0 px-2">
                <svg className="w-4 h-4 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
              
              {/* Step 3 */}
              <div className="flex-1 text-center">
                <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold shadow-lg">
                  3
                </div>
                <p className="font-medium text-gray-700">Mail Photos</p>
              </div>
              
              {/* Arrow */}
              <div className="flex-shrink-0 px-2">
                <svg className="w-4 h-4 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
              
              {/* Step 4 */}
              <div className="flex-1 text-center">
                <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-green-600 text-white flex items-center justify-center font-bold shadow-lg">
                  4
                </div>
                <p className="font-medium text-gray-700">Get Results</p>
              </div>
            </div>
            
            {/* Mobile Layout - 2x2 Grid */}
            <div className="grid grid-cols-2 gap-3 sm:hidden">
              {/* Step 1 */}
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold shadow-lg text-lg">
                  1
                </div>
                <p className="font-medium text-gray-700 text-xs">Submit<br/>Payment</p>
              </div>
              
              {/* Step 2 */}
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold shadow-lg text-lg">
                  2
                </div>
                <p className="font-medium text-gray-700 text-xs">Receive<br/>Kit</p>
              </div>
              
              {/* Step 3 */}
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold shadow-lg text-lg">
                  3
                </div>
                <p className="font-medium text-gray-700 text-xs">Mail<br/>Photos</p>
              </div>
              
              {/* Step 4 */}
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-green-600 text-white flex items-center justify-center font-bold shadow-lg text-lg">
                  4
                </div>
                <p className="font-medium text-gray-700 text-xs">Get<br/>Results</p>
              </div>
            </div>
            
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-600 leading-relaxed">
                After payment, we'll send you a shipping kit with a prepaid label included. Mail us your photos, 
                we'll digitize them (7-10 days), notify you when complete, and return your originals.
              </p>
            </div>
          </div>

          {/* Order Summary - Mobile Optimized */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-4 sm:p-6 mb-6 border-2 border-purple-200">
            <h3 className="font-bold text-base sm:text-lg mb-3 sm:mb-4 text-gray-800 flex items-center">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0 text-amber-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Order Summary
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Package:</span>
                <span className="font-semibold text-right">{packageDetails.packageName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Digitization:</span>
                <span className="font-semibold">${packageDetails.basePrice}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Shipping (Roundtrip):</span>
                <span className="font-semibold">${packageDetails.shipping.toFixed(2)}</span>
              </div>
              {packageDetails.addonCredits > 0 && (
                <div className="flex justify-between text-blue-600">
                  <span>Animation Credits:</span>
                  <span className="font-semibold text-right">+{packageDetails.addonCredits} credits (+${addonPrice})</span>
                </div>
              )}
              <div className="border-t-2 border-purple-300 pt-2 mt-2 flex justify-between text-base sm:text-lg">
                <span className="font-bold text-gray-800">Total:</span>
                <span className="font-bold text-purple-600">${totalPrice.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Photo Types Accepted */}
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-6">
            <h4 className="font-semibold text-amber-900 mb-2 flex items-center">
              <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4M12 8h.01" />
              </svg>
              Photo Types We Accept
            </h4>
            <p className="text-sm text-amber-800">
              We only digitize: <strong>4x6, 5x7, 8x10 prints (polaroids, vintage photos, and photos from albums)</strong>
            </p>
          </div>

          {/* Email Field */}
          <div className="mb-6">
            <h3 className="font-bold text-lg mb-4 text-gray-800 flex items-center">
              <svg className="w-6 h-6 mr-2 flex-shrink-0 text-gray-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Contact Email
            </h3>
            <p className="text-sm text-gray-600 mb-4">This email will be used for all communication regarding your order</p>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
              <input
                type="email"
                name="email"
                value={shippingAddress.email || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="your@email.com"
                required
              />
            </div>
          </div>

          {/* Shipping Address Form */}
          <div className="mb-6">
            <h3 className="font-bold text-lg mb-4 text-gray-800 flex items-center">
              <svg className="w-6 h-6 mr-2 flex-shrink-0 text-gray-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Your Shipping Address
            </h3>
            <p className="text-sm text-gray-600 mb-4">This address will be used to send your physical photos back to you</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={shippingAddress.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="John Doe"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
                <input
                  ref={street1Ref}
                  type="text"
                  name="street1"
                  value={shippingAddress.street1}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Start typing your address..."
                  autoComplete="off"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Apartment, Suite, etc. (optional)</label>
                <input
                  type="text"
                  name="street2"
                  value={shippingAddress.street2}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Apt 4B"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                  <input
                    type="text"
                    name="city"
                    value={shippingAddress.city}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="New York"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                  <input
                    type="text"
                    name="state"
                    value={shippingAddress.state}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="NY"
                    maxLength="2"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code *</label>
                  <input
                    type="text"
                    name="zip"
                    value={shippingAddress.zip}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="10001"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={shippingAddress.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              {/* Country - US Only */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                <select
                  name="country"
                  value="US"
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed opacity-75"
                  required
                  disabled
                >
                  <option value="US">United States</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">Currently shipping within the United States only</p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Checkout Button */}
          <button
            onClick={handleCheckout}
            disabled={processing || validating}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 rounded-xl hover:opacity-90 transition-opacity font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {validating ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Validating Address...
              </>
            ) : processing ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : (
              <>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                Proceed to Payment
              </>
            )}
          </button>

          <p className="text-xs text-center text-gray-500 mt-4">
            Secure checkout powered by Stripe. Your payment information is encrypted and secure.
          </p>
        </div>
      </div>
      
      {/* Address Correction Modal */}
      {showCorrectionModal && validationResult?.corrected && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-100 p-3 rounded-full">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Address Verified</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              We found a standardized version of your address using USPS validation. Would you like to use the corrected address?
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {/* Original Address */}
              <div className="border-2 border-gray-300 rounded-lg p-4">
                <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <span className="bg-gray-100 px-2 py-1 rounded text-sm">Original</span>
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>{validationResult.original.street1}</p>
                  {validationResult.original.street2 && <p>{validationResult.original.street2}</p>}
                  <p>{validationResult.original.city}, {validationResult.original.state} {validationResult.original.postal_code}</p>
                </div>
              </div>
              
              {/* Corrected Address */}
              <div className="border-2 border-green-500 rounded-lg p-4 bg-green-50">
                <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="bg-green-100 px-2 py-1 rounded text-sm">USPS Verified</span>
                </h4>
                <div className="text-sm text-gray-800 space-y-1 font-medium">
                  <p>{validationResult.corrected.street1}</p>
                  {validationResult.corrected.street2 && <p>{validationResult.corrected.street2}</p>}
                  <p>{validationResult.corrected.city}, {validationResult.corrected.state} {validationResult.corrected.postal_code}</p>
                </div>
              </div>
            </div>
            
            {validationResult.messages && validationResult.messages.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> {validationResult.messages.join(', ')}
                </p>
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={acceptCorrectedAddress}
                className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold"
              >
                Use Verified Address
              </button>
              <button
                onClick={keepOriginalAddress}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
              >
                Keep My Address
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailInCheckoutModal;
